import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import NRAIMorphSpinner, { CROSSFADE_DURATION } from '../../NRAIMorphSpinner/NRAIMorphSpinner';
import NRAIThinkingButton from '../../NRAIThinkingButton/NRAIThinkingButton';
import { SUGGESTED_PROMPTS, CAPABILITY_SUMMARIES } from '../prompts';

export default function WelcomeScreen({ isLoadingSession, sessionError, onPromptClick }) {
  const theme = useTheme();
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerComplete, setSpinnerComplete] = useState(false);
  const [textOpacity, setTextOpacity] = useState(1);
  const [spinnerOpacity, setSpinnerOpacity] = useState(1);
  const [activeCapabilityId, setActiveCapabilityId] = useState(
    CAPABILITY_SUMMARIES[0]?.id ?? 'vulnPrioritization'
  );

  const activeCapability = CAPABILITY_SUMMARIES.find(
    (capability) => capability.id === activeCapabilityId
  );

  // Show spinner when loading starts
  useEffect(() => {
    if (isLoadingSession) {
      setShowSpinner(true);
      setSpinnerComplete(false);
      setTextOpacity(1);
      setSpinnerOpacity(1);
    }
  }, [isLoadingSession]);

  // Complete spinner when loading finishes
  useEffect(() => {
    if (!isLoadingSession && showSpinner && !spinnerComplete) {
      setSpinnerComplete(true);
      // Fade out text immediately when completion starts
      setTextOpacity(0);
    }
  }, [isLoadingSession, showSpinner, spinnerComplete]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      {/* Session loading indicator */}
      {isLoadingSession && (
        <Box 
          sx={{ 
            mb: 2, 
            display: 'flex', 
            alignItems: 'center',
            opacity: spinnerOpacity,
            transition: `opacity ${CROSSFADE_DURATION}ms ease-out`,
            '& > *': {
              '& .MuiTypography-root': {
                opacity: textOpacity,
                transition: `opacity ${CROSSFADE_DURATION}ms ease-out`
              }
            }
          }}
        >
          <NRAIThinkingButton
            isThinking={isLoadingSession}
            label="Initializing session..."
            thinkingLabel="Initializing session..."
            color={theme.palette.text.secondary}
            showChevron={false}
            asText={true}
          />
        </Box>
      )}

      {/* Session error indicator */}
      {sessionError && !isLoadingSession && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 2,
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.contrastText,
            borderRadius: theme.shape.borderRadius * 2,
            maxWidth: '500px'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Session Error
          </Typography>
          <Typography variant="caption">{sessionError}</Typography>
        </Paper>
      )}

      <Box 
        sx={{ 
          mb: 3, 
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            width: theme.spacing(10),
            height: theme.spacing(10),
            display: 'flex',
            padding: theme.spacing(2),
            justifyContent: 'center',
            alignItems: 'center',
            gap: theme.spacing(1),
            borderRadius: theme.spacing(8),
            boxShadow: `0 2px 8px 0 ${alpha(theme.palette.riseAiPurple, 0.25)}`
          }}
        >
          <NRAIMorphSpinner 
            size={48} 
            color={theme.palette.riseAiPurple}
            complete={!isLoadingSession}
          />
        </Box>
      </Box>
      <Typography variant="h3" sx={{ mb: theme.spacing(3) }}>
        Welcome. I have analyzed your software asset.
      </Typography>
      <Typography variant="body1" sx={{ mb: theme.spacing(3), color: theme.palette.text.secondary, textAlign: 'center'}}>
        I'm ready to help you validate findings, explain complex issues, and build remediation workflows.
        <br />
        Below are some prompts to help start, or you can ask anything related to software security.
      </Typography>

      <Divider
        textAlign="center"
        sx={{
          width: '100%'
        }}
      >
        <Typography variant="label" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
          WHAT I CAN DO
        </Typography>
      </Divider>

      {/* Capability labels (chips) */}
      <Box
        sx={{
          mt: theme.spacing(3),
          mb: theme.spacing(3),
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'center'
        }}
      >
        {CAPABILITY_SUMMARIES.map((capability) => {
          const isActive = capability.id === activeCapabilityId;

          return (
            <Box
              key={capability.id}
              component="button"
              type="button"
              onClick={() => setActiveCapabilityId(capability.id)}
              sx={{
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                padding: theme.spacing(0.5, 1.5),
                alignItems: 'center',
                gap: theme.spacing(0.5),
                borderRadius: theme.spacing(3),
                background: isActive
                  ? theme.palette.riseAiPurpleBackground
                  : theme.palette.mode === 'dark'
                    ? theme.palette.neutral[700]
                    : theme.palette.neutral[200],
                color:
                  theme.palette.mode === 'dark'
                    ? isActive
                      ? theme.palette.secondary.contrastText
                      : theme.palette.primary
                    : theme.palette.primary,
              }}
            >
              <Typography
                variant="body2"
              >
                {capability.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Active capability description */}
      {activeCapability && (
        <Typography
          variant="body1"
          sx={{
            mb: theme.spacing(3),
            maxWidth: theme.spacing(90),
            textAlign: 'center',
            color: theme.palette.text.secondary
          }}
        >
          {activeCapability.description}
        </Typography>
      )}

      <Divider
        textAlign="center"
        sx={{
          width: '100%',
        }}
      >
        <Typography variant="label" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
          OR TRY A PROMPT
        </Typography>
      </Divider>
      {/* Prompt Cards */}
      <Box sx={{ display: 'flex', gap: theme.spacing(3), width: '100%', justifyContent: 'center', flexWrap: 'wrap', mt: theme.spacing(3) }}>
        {SUGGESTED_PROMPTS.map((prompt, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              flex: '1 1 200px',
              minWidth: '200px',
              minHeight: '216px',
              '&:hover': {
                boxShadow: theme.shadows[4],
                borderColor: theme.palette.riseAiPurple
              }
            }}
            onClick={() => onPromptClick(prompt.text)}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: theme.spacing(6),
                    height: theme.spacing(6),
                    borderRadius: theme.spacing(1),
                    backgroundColor: theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <prompt.icon height="32" width="32" color={theme.palette.text.iconInfo} />
                </Box>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {prompt.title}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                {prompt.text}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
